import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
 signUp,
 login,
 refreshToken,
 forgotPassword,
 resetPassword,
 googleSignin,
} from "../../core/api";
import type { RootState } from "../store";
import { LocalStorage } from "../../types/local_storage.enum";
import { UserProfile } from "../../types/user.type";
import { toast } from "react-toastify";

export interface AuthState {
 token: string | null;
 refreshToken: string | null;
 user: UserProfile | null;
 status: "idle" | "loading" | "succeeded" | "failed";
 error: string | null;
}

const initialState: AuthState = {
 token: localStorage.getItem("token") || null,
 refreshToken: localStorage.getItem("refreshToken") || null,
 user: null,
 status: "idle",
 error: null,
};

// SIGNUP
export const signUpUser = createAsyncThunk(
 "auth/signUpUser",
 async (formData: FormData, { rejectWithValue }) => {
  try {
   const response = await signUp(formData);
   return response.data;
  } catch (error: any) {
   return rejectWithValue(error.response?.data || "Sign up failed");
  }
 }
);

// GOOGLE SIGNIN
export const googleSignUp = createAsyncThunk(
 "auth/googleSignUp",
 async (token: string, { rejectWithValue }) => {
  try {
   const response = await googleSignin(token);
   return response.data;
  } catch (error: any) {
   return rejectWithValue(error.response?.data || "Sign up failed");
  }
 }
);
// LOGIN
export const loginUser = createAsyncThunk(
 "auth/loginUser",
 async (
  credentials: { UserName: string; Password: string },
  { rejectWithValue }
 ) => {
  try {
   const response = await login(credentials);
   return response.data;
  } catch (error: any) {
   return rejectWithValue(error.response?.data || "Login failed");
  }
 }
);

// REFRESH
export const refreshAuthToken = createAsyncThunk(
 "auth/refreshAuthToken",
 async (_, { getState, rejectWithValue }) => {
  try {
   const state = getState() as RootState;
   const { token, refreshToken: rt } = state.auth;
   if (!token || !rt) {
    throw new Error("No token or refreshToken found");
   }
   const response = await refreshToken({ Token: token, RefreshToken: rt });
   return response.data;
  } catch (error: any) {
   return rejectWithValue(error.response?.data || "Refresh token failed");
  }
 }
);

export const forgotPasswordUser = createAsyncThunk(
 "auth/forgotPasswordUser",
 async (email: string, { rejectWithValue }) => {
  try {
   const response = await forgotPassword(email);
   return response.data;
  } catch (error: any) {
   return rejectWithValue(
    error.response?.data || "Forgot password request failed"
   );
  }
 }
);

// RESET PASSWORD
export const resetUserPassword = createAsyncThunk(
 "auth/resetUserPassword",
 async (
  payload: {
   email: string;
   otp: string;
   newPassword: string;
   confirmPassword: string;
  },
  { rejectWithValue }
 ) => {
  try {
   const response = await resetPassword({
    email: payload.email,
    oldPassword: payload.otp,
    newPassword: payload.newPassword,
    confirmPassword: payload.confirmPassword,
   });
   return response.data;
  } catch (error: any) {
   return rejectWithValue(error.response?.data || "Reset password failed");
  }
 }
);

export const authSlice = createSlice({
 name: "auth",
 initialState,
 reducers: {
  clearError: (state) => {
   state.error = null;
  },
  logout: (state) => {
   function clearCookies() {
    document.cookie.split(";").forEach((cookie) => {
     document.cookie = cookie
      .replace(/^ +/, "")
      .replace(/=.*/, "=;expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/");
    });
   }
   clearCookies();
   state.token = null;
   state.refreshToken = null;
   state.user = null;
   state.status = "idle";
   state.error = null;
   localStorage.removeItem("token");
   localStorage.removeItem("user");
   localStorage.removeItem("refreshToken");
   localStorage.removeItem(LocalStorage.LOCAL_INTERVIEWD_LIST);
  },
  setCredentials: (state, action) => {
   state.token = action.payload.token;
   state.refreshToken = action.payload.refreshToken;
  },
  setUser: (state, action) => {
   localStorage.setItem(LocalStorage.USER, JSON.stringify(action.payload));
   state.user = action.payload;
  },
 },
 extraReducers: (builder) => {
  builder
   .addCase(signUpUser.pending, (state) => {
    state.status = "loading";
    state.error = null;
   })
   .addCase(signUpUser.fulfilled, (state, action) => {
    state.status = "succeeded";
    state.token = action.payload.token;
    state.refreshToken = action.payload.refreshToken;
    state.user = action.payload.user;

    localStorage.setItem(
     LocalStorage.USER,
     JSON.stringify(action.payload.user)
    );
    localStorage.setItem("token", action.payload.token);
    localStorage.setItem("refreshToken", action.payload.refreshToken);
   })
   .addCase(googleSignUp.pending, (state) => {
    state.status = "loading";
    state.error = null;
   })
   .addCase(googleSignUp.fulfilled, (state, action) => {
    state.status = "succeeded";
    state.token = action.payload.token;
    state.refreshToken = action.payload.refreshToken;
    state.user = action.payload.user;
    if (action.payload.user) {
     toast.success("Login successful");
     localStorage.setItem("user", JSON.stringify(action.payload.user));
    }
    localStorage.setItem(
     LocalStorage.USER,
     JSON.stringify(action.payload.user)
    );
    localStorage.setItem("token", action.payload.token);
    localStorage.setItem("refreshToken", action.payload.refreshToken);
   })
   .addCase(googleSignUp.rejected, (state, action) => {
    state.status = "failed";
    state.error = action.payload as string;
   })
   .addCase(signUpUser.rejected, (state, action) => {
    state.status = "failed";
    state.error = action.payload as string;
   })

   .addCase(loginUser.pending, (state) => {
    state.status = "loading";
    state.error = null;
   })
   .addCase(loginUser.fulfilled, (state, action) => {
    state.status = "succeeded";
    state.token = action.payload.token;
    state.refreshToken = action.payload.refreshToken;
    state.user = action.payload.user;
    localStorage.setItem(
     LocalStorage.USER,
     JSON.stringify(action.payload.user)
    );
    localStorage.setItem("token", action.payload.token);
    localStorage.setItem("refreshToken", action.payload.refreshToken);
   })
   .addCase(loginUser.rejected, (state, action) => {
    state.status = "failed";
    state.error = action.payload as string;
   })

   .addCase(refreshAuthToken.fulfilled, (state, action) => {
    state.token = action.payload.token;
    state.refreshToken = action.payload.refreshToken;
    localStorage.setItem("token", action.payload.token);
    localStorage.setItem("refreshToken", action.payload.refreshToken);
   })

   .addCase(forgotPasswordUser.pending, (state) => {
    state.status = "loading";
    state.error = null;
   })
   .addCase(forgotPasswordUser.fulfilled, (state) => {
    state.status = "succeeded";
   })
   .addCase(forgotPasswordUser.rejected, (state, action) => {
    state.status = "failed";
    state.error = action.payload as string;
   })

   // RESET PASSWORD
   .addCase(resetUserPassword.pending, (state) => {
    state.status = "loading";
    state.error = null;
   })
   .addCase(resetUserPassword.fulfilled, (state) => {
    state.status = "succeeded";
   })
   .addCase(resetUserPassword.rejected, (state, action) => {
    state.status = "failed";
    state.error = action.payload as string;
   });
 },
});

export const { logout, clearError, setUser, setCredentials } =
 authSlice.actions;
export default authSlice.reducer;
